import React from 'react';
import { observer } from 'mobx-react-lite';
import { useUpdateScreenStates } from '../../hooks/useUpdateScreenStates';
import { viewStore } from '../../../stores/ViewStore';
import { screenStore } from '../../../stores/ScreenStore';
import { workspaceStore } from '../../../stores/WorkspaceStore';
import SearchResult from './SearchResult/SearchResult';
import Map from './Map/Map';
import { ResponseData, MarkerNew, RequestParams } from '../../types/search';
import { getMembershipPrices, scrollToTop, parseQueryString, getMaxWorkspaceCount, switches } from './ResultsList.utils';
import styles from './ResultsList.module.scss';
import { ReactComponent as Chevron } from '../../../assets/icons/chevron.svg';
import FiltersPanel from './FiltersPanel/FiltersPanel';
import Pagination from './Pagination/Pagination';
import SideFilters from './FiltersPanel/SideFilters/SideFilters';
import { useResultListContext } from 'common/contexts/ResultListContext';
import { pathToIcon } from './Map/ui-components/customIcons';
import noResultsImage from '../../../assets/img/no_results_image.png';
import FiltersList from './FiltersPanel/FiltersList/FiltersList';
import ViewToggle from './FiltersPanel/ViewToggle/ViewToggle';
import Loader from './Map/ui-components/Loader';
import { useLocation, useNavigate } from 'react-router-dom';
import {ReactComponent as Arrow} from '../../../assets/icons/arrowBold.svg'

const ResultsListRedesign: React.FC = () => {
  // allow loading on search results page only
  // @ts-ignore
  if (!window['hydrateJSON']?.['reactResults']) {
    return null;
  }

  useUpdateScreenStates();
  const { isTablet, isLaptop } = screenStore;
  const { view, setView } = viewStore;
  const { anyWorkspaceChecked, setAnyWorkspaceChecked } = workspaceStore;

  const { 
    workspaceType,
    setWorkspaceType,
    currentDuration,
    setCurrentDuration,
    currentLocation,
    setCurrentLocation,
    setCurrentResources,
    durationOptions,
    setDurationOptions,
    spacesCount,
    setSpacesCount,
    setActiveFilters,
    setCurrentPage,
    sortBy,
    budgetValues,
    setBudgetValues,
    budgetRange,
    setBudgetRange,
    setHistogramValues,
    setSortBy,
    setCurrentAmenities,
    breadcrumbs,
    setBreadcrumbs,
    firstLoad,
    setFirstLoad,
    setOnlyWorkspaceSelected,
    setAvailableAmenities,
  } = useResultListContext();
  const location = useLocation();
  const navigate = useNavigate();

  const [generalData, setGeneralData] = React.useState<ResponseData | null>(null);
  const [initialLocation, setInitialLocation] = React.useState({ lat: 0, lng: 0, rad: 0 });
  const [currentMarker, setCurrentMarker] = React.useState<number | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [hasNoResults, setHasNoResults] = React.useState(false);
  const [hasResults, setHasResults] = React.useState(false);
  const maxValue = getMaxWorkspaceCount(workspaceType, spacesCount, anyWorkspaceChecked);
  const [mapCenterNo, setMapCenterNo] = React.useState(0);
  const [fetchCount, setFetchCount] = React.useState(0);

  // @ts-ignore
  const baseURL = window?.hydrateJSON?.baseUrl;

  // @ts-ignore
  const locationName = window?.hydrateJSON?.locationName;

  // @ts-ignore
  // const countryName = window?.hydrateJSON?.countries[0].name || generalData?.spaces[0].location.country_name || generalData?.spaces[0].location.country_code;

  // @ts-ignore
  const cityId = window?.hydrateJSON?.cityId;

  // @ts-ignore
  const countryId = window?.hydrateJSON?.countryId;

  // @ts-ignore
  const isVirtualOfficePage = Object.keys(window.hydrateJSON.resourceTypesToFilter).length === 1 && Object.keys(window.hydrateJSON.resourceTypesToFilter)[0] === 'virtual_offices' && firstLoad;

  React.useEffect(() => {
    viewStore.setLocationName(locationName);
  }, [locationName]);

  const backToListingRequest = async () => {
    let lastRequest = sessionStorage.getItem('lastRequest');
    if (lastRequest) {
      let url = new URL(lastRequest);
      let params = new URLSearchParams(url.search);
      let page = params.get('page');
      let resources = params.get('resource_types');
      let duration = params.get('duration');
      let amenities = params.get('amenities');
      // @ts-ignore
      let budget = [!!params.get('min_price') && params.get('min_price') != 'undefined' ? params.get('min_price') : 0, !!params.get('max_price') && params.get('max_price') != 'undefined' ? params.get('max_price') : Number(window?.hydrateJSON?.initial_data.counts.prices.highest_price)];
      let sort = params.get('sort_by');

      try {
        const response = await fetch(lastRequest);
        const json = await response.json();

        setView(sessionStorage.getItem('view') || 'list');
        setCurrentPage(Number(page) + 1 || 1);

        if (duration) {
          const durations = duration.split(',');
          setDurationOptions(durationOptions.map(option => ({
            ...option,
            isActive: durations.includes(option.val),
          })));
        }

        if (resources) {
          const resourcesArray = resources.split(',');
          let newCheckedState: Record<string, boolean> = {};
          for (let key in switches) {
            if (resourcesArray.includes(switches[key as keyof typeof switches])) {
              newCheckedState[key] = true;
            }
          }
          setWorkspaceType(newCheckedState);

          let url = new URL(window.location.href);
          let params = url.searchParams;
          params.delete('filter_resource_types');
          params.set('filter_resource_types', resourcesArray.join(','));
          window.history.replaceState({}, '', url.toString());
        }

        if (amenities) {
          const amenitiesArray = amenities.split(',');
          let newActiveFilters: Record<string, boolean> = {};
          amenitiesArray.forEach((amenityId) => {
            newActiveFilters[amenityId] = true;
          });
          setActiveFilters(newActiveFilters);
        }

        setBudgetRange([Number(json.counts.prices.lowest_price), Number(json.counts.prices.highest_price)]);
        setHistogramValues(json.counts.prices.prices_histogram);
        if (budget[0] || budget[1]) {
          setBudgetValues([Number(budget[0]), Number(budget[1])])
        } else {
          setBudgetValues([Number(json.counts.prices.lowest_price), Number(json.counts.prices.highest_price)]);
        }

        if (sort) {
          setSortBy(sort)
        }

        setGeneralData(json);
        setSpacesCount(json.filtered_counts);
        setFirstLoad(false);
        setTimeout(() => {
          sessionStorage.setItem('useLastRequest', 'false');
        }, 2000)
      } catch (error) {
        console.error(error);
      }
    }
  }
  // Receiving the API data
  // @ts-ignore
  const fetchData = async (lat:number, lng:number, rad = 100, perPage = 10, page = 0, resourceTypes = [], amenities = '', duration = '', budgetValues = [Number(window.hydrateJSON?.initial_data.counts.prices.lowest_price) || budgetRange[0], Number(window.hydrateJSON?.initial_data.counts.prices.highest_price) || budgetRange[1]], sortBy = '') => {
    if (sessionStorage.getItem('useLastRequest') === 'true') {
      backToListingRequest();
    } else {
      if (lat === 0 && lng === 0) {
        lat = initialLocation.lat;
        lng = initialLocation.lng;
      }
      if (rad === 100 && initialLocation.rad !== 0) {
        rad = initialLocation.rad;
      }
      setGeneralData(null);
      setCurrentLocation({ rad, lat, lng });

      // @ts-ignore
      const minExistingPrice = Number(window.hydrateJSON?.initial_data.counts.prices.lowest_price);
      // @ts-ignore
      const maxExistingPrice = Number(window.hydrateJSON?.initial_data.counts.prices.highest_price);
      
      // @ts-ignore 
      const urlResources = window.hydrateJSON.queryString;
      if (lat !== 0 || lng !== 0) {
        try {
          const amenitiesFiltered = amenities ? amenities?.split('=')[1].split(',').filter((el) => el.replace(/[^0-9]/g, '')).join(',') : '';
          const requestUrl = `${baseURL}ajax/nearbyspaces?` +
            `lat=${lat}` +
            `&lon=${lng}` +
            `&rad=${rad}` +
            `&per_page=${view === 'map' ? 50 : perPage}` +
            `&split=10&page=${page}` +
            `&resource_types=${isVirtualOfficePage ? 'virtual_offices' : resourceTypes.join(',')}` +
            `&amenities=${amenitiesFiltered}` +
            `${duration}` +
            `&cityId=${cityId}` +
            `&countryId=${countryId}` +
            `&view=${view}` +
            `&min_price=${budgetValues[0]}` +
            `&max_price=${budgetValues[1]}` +
            `&sort_by=${sortBy}`;
      
          const searchParams = new URLSearchParams(location.search);
          const minBudgetValue = searchParams.get('min_price') || '';
          const maxBudgetValue = searchParams.get('max_price') || '';

          // setting url params start
          const requestParams: RequestParams = {
            page,
            cityId,
            countryId,
            view,
            min_price: firstLoad ? +minBudgetValue : budgetValues[0],
            max_price: firstLoad ? +maxBudgetValue : budgetValues[1],
            sort_by: sortBy,
          };

          if (firstLoad && urlResources) {
            requestParams.filter_resource_types = urlResources;
          }
          
          if (resourceTypes) {
            requestParams.filter_resource_types = resourceTypes.join(',');
          }

          if (amenities) {
            requestParams.amenities = amenities.split('=')[1];
          }
          if (duration) {
            requestParams.duration = duration.split('=')[1];
          }
          for (const [param, value] of Object.entries(requestParams)) {
            if (param == 'page' && value != 0) {
              searchParams.set(param, value);
            }
            if (param == 'min_price' && value != 0 && value != minExistingPrice) {
              searchParams.set(param, value);
            }
            if (param == 'max_price' && value != 0) {
              searchParams.set(param, value);
            }
            if (param == 'sort_by' && value != '') {
              searchParams.set(param, value);
            }
            if (param == 'filter_resource_types' && value != '') {
              searchParams.set(param, value);
            }
            if (param == 'amenities' && value != 'undefined') {
              searchParams.set(param, value);
            }
            if (param == 'duration' && value != '') {
              searchParams.set(param, value);
            }
            if (param == 'view') {
              searchParams.set(param, value);
            }
            if (param == 'view' && value != 'list') {
              searchParams.set(param, value);
            }
            //searchParams.set(param, value);
          }
          const pathname = location.pathname;
          navigate(`${pathname}?${searchParams.toString()}`);
          // setting url params end
          
          const response = await fetch(requestUrl);
          const json = await response.json();
    
          sessionStorage.setItem('lastRequest', requestUrl);
          sessionStorage.setItem('lastUrl', window.location.href);
          sessionStorage.setItem('view', view);
    
          setGeneralData(json);
          setSpacesCount(json.filtered_counts);
          setCurrentPage(page + 1);

          if (json.spaces !== 'no results') {
            setAvailableAmenities(json.available_amenities);
            localStorage.setItem('availableAmenities', JSON.stringify(json.available_amenities));
          }

          if (view === 'map') {
            if (cityId === 0) {
              if (json.markers.length === 0) {
                if (fetchCount === 0) {
                  setMapCenterNo(1);
                } else if (fetchCount === 1) {
                  setMapCenterNo(2);
                }
              }
            }
            setFetchCount(fetchCount + 1);
          }

          setFirstLoad(false);

          if (Number(json.counts.prices.lowest_price) !== budgetRange[0] || Number(json.counts.prices.highest_price !== budgetRange[1])) {
            setBudgetRange([Number(json.counts.prices.lowest_price), Number(json.counts.prices.highest_price)]);
            setHistogramValues(json.counts.prices.prices_histogram);
          }
        } catch (error) {
          console.error(error);
        }
      }
    }
  };
  React.useEffect(() => {
    const amenitiesFromStorage = localStorage.getItem('availableAmenities');
    if (amenitiesFromStorage) {
      try {
        const parsedAmenities = JSON.parse(amenitiesFromStorage);
        setAvailableAmenities(parsedAmenities);
      } catch (error) {
        console.error('Failed to parse available amenities from storage', error);
      }
    }
  }, []);
  // fetch new data on first load
  React.useEffect(() => {
    const elementToRemove = document.querySelector('.main_react_container');
    if (elementToRemove) {
      elementToRemove.remove();
    }
    // @ts-ignore
    const hJSON = window.hydrateJSON;
    let lat = 0;
    let lng = 0;
    let rad = 100;
    if (hJSON && hJSON.centerCoordinatesJSON) {
      [lat, lng] = hJSON.centerCoordinatesJSON;
      rad = 100;
      if (hJSON.locationParams) {
        lat = hJSON.locationParams.lat;
        lng = hJSON.locationParams.lng;
        rad = hJSON.locationParams.radius;
      }
      setInitialLocation({ lat, lng, rad });
      setCurrentLocation({ rad, lat, lng });
    }
    setBreadcrumbs(hJSON.breadcrumbs);

    // apply workspace filters on the first load after we chose it on home page 
    const searchParams = new URLSearchParams(location.search);
    const workspaceFilters = searchParams.get('filter_resource_types') || '';
    const { checkedState: newCheckedState, activeKeysCount, activeResources } = parseQueryString(workspaceFilters, switches);
    if (isVirtualOfficePage) {
      setWorkspaceType({virtualOfficeCount: true});
      setCurrentResources(['virtual_offices']);
    } else {
      setWorkspaceType(newCheckedState);
      if (activeKeysCount === 0 || activeKeysCount === Object.keys(switches).length) {
        setAnyWorkspaceChecked(true);
      } else {
        setAnyWorkspaceChecked(false);
      }
      setCurrentResources(activeResources);
    }
    

    // duration
    const durationFilters = searchParams.get('duration') || '';
    setDurationOptions([
      { title: 'Daily', isActive: durationFilters.includes('day'), val: 'day' },
      { title: 'Monthly', isActive: durationFilters.includes('month'), val: 'month' },
      { title: 'Annually', isActive: durationFilters.includes('year'), val: 'year' },
    ]);

    // view
    const viewFilter = searchParams.get('view') || '';
    // @ts-ignore
    if (window.CRO_899 !== 2) {
      setView(viewFilter.length > 0 ? viewFilter : 'list');
    }
    // @ts-ignore
    if (window.CRO_899 !== 2) {
      setView(viewFilter.length > 0 ? viewFilter : 'list');
    }

    // amenities
    const amenitiesFilter = searchParams.get('amenities') || '';
    const valuesArray = amenitiesFilter.split(',');
    const amenitiesPayload:Record<number, boolean> = {};
    valuesArray.forEach(value => {
      amenitiesPayload[parseInt(value)] = true;
    });
    setActiveFilters(amenitiesPayload);
    setCurrentAmenities(`&amenities=${amenitiesFilter}`)

    // sort
    const sortFilter = searchParams.get('sort_by') || '';
    setSortBy(sortFilter);
    // page 
    const pageFilter = searchParams.get('page') || '';
    setCurrentPage(Number(pageFilter));

    // budget 
    // @ts-ignore
    const initialBudget = [Number(window.hydrateJSON?.initial_data.counts.prices.lowest_price), Number(window.hydrateJSON?.initial_data.counts.prices.highest_price)]
    setBudgetRange(initialBudget);
    setBudgetValues([Number(searchParams.get('min_price')) || initialBudget[0], Number(searchParams.get('max_price')) || initialBudget[1]]);
    // @ts-ignore
    setHistogramValues(window.hydrateJSON?.initial_data.counts.prices.prices_histogram);
    const filteredBudget = [Number(searchParams.get('min_price')) || initialBudget[0], Number(searchParams.get('max_price')) || initialBudget[1]];

    if (view !== 'map') {
      fetchData(lat, lng, rad, 10, +pageFilter, 
        activeResources as any,
        amenitiesFilter && `&amenities=${amenitiesFilter}`,
        durationFilters && `&duration=${durationFilters}`,
        filteredBudget,
        sortFilter);
    }
  }, []);

  React.useEffect(() => {
    if (generalData) {
      setIsLoading(false);
      if (Array.isArray(generalData.spaces)) {
        setHasResults(true);
        setHasNoResults(false);
      } else if (generalData.spaces === 'no results') {
        setHasResults(false);
        setHasNoResults(true);
      }
    } else {
      setIsLoading(true);
    }
  }, [generalData]);

  // fetch new data if we toggle the view
  React.useEffect(() => {
    setGeneralData(null);
    setCurrentPage(1);
    const searchParams = new URLSearchParams(location.search);
    const amenitiesFilter = '&amenities='+searchParams.get('amenities')?.replace(/^,+/g, '').replace(/,+/g, ',').replace(/,$/g, '').replace(/^undefined,|,undefined$|,undefined,/g, '').replace(/NaN|NaN,|,NaN$|,NaN,/g, '') || '';
    if (view === 'list' && sessionStorage.getItem('useLastRequest') !== 'true' && !firstLoad) {
      fetchData(
        initialLocation.lat,
        initialLocation.lng,
        initialLocation.rad,
        10,
        0,
        // @ts-ignore
        Object.entries(workspaceType).filter((el) => el[1] == true).map(item => switches[item[0]]),
        amenitiesFilter,
        currentDuration,
        budgetValues,
        sortBy,
      );
    }
  }, [view]);

  const onWorkspaceHandler = (resourceTypes = [], reset: boolean = false) => {
    const searchParams = new URLSearchParams(location.search);
    const amenitiesFilter = '&amenities='+searchParams.get('amenities')?.replace(/^,+/g, '').replace(/,+/g, ',').replace(/,$/g, '').replace(/^undefined,|,undefined$|,undefined,/g, '').replace(/NaN|NaN,|,NaN$|,NaN,/g, '') || '';
    if (reset) {
      setCurrentResources([]);
      setAnyWorkspaceChecked(false);
      setCurrentAmenities('');
      // hack to clear filters
      const pathname = location.pathname;
      navigate(`${pathname}`);
      setFirstLoad(true);
      fetchData(currentLocation.lat, currentLocation.lng, currentLocation.rad, 10, 0, [], '', '', [], '');
    } else {
      fetchData(
        currentLocation.lat,
        currentLocation.lng,
        currentLocation.rad,
        10,
        0,
        resourceTypes,
        amenitiesFilter,
        currentDuration,
        budgetValues,
        sortBy,
      );
    }
  };

  const handleDurationSelection = (index:number, reset: boolean = false):any => {
    if (reset) {
      setDurationOptions([
        { title: 'Daily', isActive: false, val: 'day' },
        { title: 'Monthly', isActive: false, val: 'month' },
        { title: 'Annually', isActive: false, val: 'year' },
      ]);
    } else {
      setDurationOptions(
        durationOptions.map((option, i) => {
          if (index === i) {
            return { ...option, isActive: !option.isActive };
          }
  
          return option;
        }),
      );
    }
  };

  React.useEffect(() => {
    const durationParam = durationOptions
      .filter((option) => option.isActive)
      .map((option) => option.val)
      .join(',');
    const request = `&duration=${durationParam}`;
    setCurrentDuration(request);
    setCurrentPage(1);
    const searchParams = new URLSearchParams(location.search);
    const amenitiesFilter = '&amenities='+searchParams.get('amenities')?.replace(/^,+/g, '').replace(/,+/g, ',').replace(/,$/g, '').replace(/^undefined,|,undefined$|,undefined,/g, '').replace(/NaN|NaN,|,NaN$|,NaN,/g, '') || '';
    
    if (sessionStorage.getItem('useLastRequest') !== 'true' && !firstLoad) {
      fetchData(
        currentLocation.lat,
        currentLocation.lng,
        currentLocation.rad,
        10,
        0,
        // @ts-ignore
        Object.entries(workspaceType).filter((el) => el[1] == true).map(item => switches[item[0]]),
        amenitiesFilter,
        request,
        budgetValues,
        sortBy,
      );
    }
  }, [durationOptions]);

  React.useEffect(() => {
    if (!firstLoad) {
      const searchParams = new URLSearchParams(location.search);
      const amenitiesFilter = '&amenities='+searchParams.get('amenities')?.replace(/^,+/g, '').replace(/,+/g, ',').replace(/,$/g, '').replace(/^undefined,|,undefined$|,undefined,/g, '').replace(/NaN|NaN,|,NaN$|,NaN,/g, '') || '';

      fetchData(
        currentLocation.lat,
        currentLocation.lng,
        currentLocation.rad,
        10,
        0,
        // @ts-ignore
        Object.entries(workspaceType).filter((el) => el[1] == true).map(item => switches[item[0]]),
        amenitiesFilter,
        currentDuration,
        budgetValues,
        sortBy,
      );
    }
  }, [sortBy])

  React.useEffect(() => {
    const singleWorkspaceSelected = Object.values(workspaceType).filter((active) => !!active).length == 1;
    const loungeNotSelected = !workspaceType['loungeCount'];

    if (singleWorkspaceSelected && loungeNotSelected) {
      setOnlyWorkspaceSelected(true);
    } else {
      setOnlyWorkspaceSelected(false);
    }
  }, [workspaceType])

  // Map markers
  const markers: MarkerNew[] | undefined = generalData?.markers.map((marker) => ({
    position: [marker.location.lat, marker.location.lng],
    id: marker.cs_id,
  }));

  const onPageChange = (page:number) => {
    setCurrentPage(page);
    setGeneralData(null);
    const searchParams = new URLSearchParams(location.search);
    const amenitiesFilter = '&amenities='+searchParams.get('amenities')?.replace(/^,+/g, '').replace(/,+/g, ',').replace(/,$/g, '').replace(/^undefined,|,undefined$|,undefined,/g, '').replace(/NaN|NaN,|,NaN$|,NaN,/g, '') || '';

    fetchData(
      currentLocation.lat,
      currentLocation.lng,
      currentLocation.rad,
      10,
      page - 1,
      // @ts-ignore
      Object.entries(workspaceType).filter((el) => el[1] == true).map(item => switches[item[0]]),
      amenitiesFilter,
      currentDuration,
      budgetValues,
      sortBy,
    );
  
    scrollToTop('[class*="ResultsList_container__content__results"]');
  };

  const renderContent = () => {
    if (isLoading) {
      return <Loader />
    }

    if (hasResults && generalData && generalData.spaces && Array.isArray(generalData.spaces)) {
      return generalData?.spaces?.map(
          ({ amenities_ids, id, currency_code, images, reviews_summary, reviews_full, descriptions, resource_types, show_popular, profile_url_full, display_name, memberships, transportation_links, location }: any, index: number) => {
            // eslint-disable-next-line
            const star_rating = reviews_summary.overall_rating_data.rating;
            const revs = reviews_summary.review_count;
            const description = descriptions[0].text_full;
            const currentActiveDuration = durationOptions.find((duration) => duration.val == 'month')?.isActive ? 'month' : 
            durationOptions.find((duration) => duration.val == 'day')?.isActive ? 'day' : 
            durationOptions.find((duration) => duration.val == 'year')?.isActive ? 'year' : 'month';
            const prices = getMembershipPrices(memberships, workspaceType, currentActiveDuration)

            return (
              <>
                {index === 0 && generalData && (
                  <>
                    <div className={styles.header_top}>
                      <div className={styles.header_top_left}>

                        { window.location.href.indexOf("virtual-offices") !== -1 &&
                            <h1 className={styles.header}>
                              {`Virtual Offices in ${locationName}`}
                            </h1>
                        }
                        { window.location.href.indexOf("virtual-offices") === -1 &&
                            <h1 className={styles.header}>
                              {`Coworking Spaces in ${locationName}`}
                            </h1>
                        }
                        <p className={styles.header_spaces_amount}>
                          <div className={styles.header_pin} dangerouslySetInnerHTML={{ __html: pathToIcon }} />
                          {`${spacesCount.numSpaces} results`}
                        </p>
                        {breadcrumbs.length > 0 && (
                          <div className={styles.breadcrumbs}>
                            {breadcrumbs.map((crumb, index) => (
                              <React.Fragment key={index}>
                                {index > 0 && <Chevron />}
                                <a href={crumb.name == 'New York' ? breadcrumbs[0].url : crumb.url} className={index === 0 ? styles.country : styles.city}>
                                  {crumb.name}
                                </a>
                              </React.Fragment>
                            ))}
                          </div>
                        )}
                      </div>
                      {!isTablet && view == 'list' && <div className={styles.header_top_right}>
                        <ViewToggle />
                      </div>}
                    </div>
                    <div className={styles.header_bottom}>
                      <FiltersList
                        fetchData={fetchData}
                        handleDurationSelection={handleDurationSelection}
                        onWorkspaceHandler={onWorkspaceHandler}
                      />
                    </div>
                  </>
                )}
                <SearchResult
                  key={id}
                  cs_id={id}
                  currency={currency_code}
                  description={description}
                  header={display_name}
                  images={images}
                  isPopular={show_popular}
                  link={profile_url_full}
                  monthlyPrice={prices}
                  rating={{ revs, star_rating }}
                  resources={resource_types}
                  setCurrentMarker={setCurrentMarker}
                  transportation_links={transportation_links}
                  country={location.country_name}
                  view={view}
                  amenities_ids={amenities_ids}
                  reviews_full={reviews_full}
                />
                {index === generalData?.spaces?.length - 1 && generalData && (
                  <>
                    {!(isTablet && view === 'map') && !hasNoResults && <Pagination numPages={generalData?.pagination.numPages} onPageChange={onPageChange} />}
                    {spacesCount.virtualOfficeCount > 0 && !window.location.pathname.includes("virtual-offices") &&
                    <a
                      className={styles.crosslink}
                      href={`${baseURL.replace(/\/$/, '')}${
                        window.location.pathname.includes("virtual-offices")
                          ? ''
                          : '/virtual-offices'
                      }${
                        window.location.pathname.split("/").length > 4 &&
                        !window.location.pathname.includes("united-states")
                          ? window.location.pathname.split("/").slice(0, -1).join("/")
                          : window.location.pathname
                      }`}
                      target="_blank"
                    >
                      <div>
                        <span>Looking for a virtual office?</span>
                        <p>
                          Uncover a diverse range of virtual office locations in our portfolio for a
                          seamless business experience.
                        </p>
                      </div>
                      <div>
                        <Arrow />
                      </div>
                    </a>
                    }
                  </>
                )}
                {index === generalData?.spaces?.length - 1 && generalData && (
                    <>
                      {spacesCount.virtualOfficeCount > 0 && window.location.pathname.includes("virtual-offices") &&
                          <a
                              className={styles.crosslink}
                              href={`${window.location.href.replace(/\/virtual-offices/g, "")}`}
                              target="_blank"
                          >
                            <div>
                              <span>Looking for a coworking space?</span>
                              <p>
                                Uncover a diverse range of coworking space options in our portfolio for a seamless business experience
                              </p>
                            </div>
                            <div>
                              <Arrow />
                            </div>
                          </a>
                      }
                    </>
                )}
              </>
            );
          },
        );
    }

    if (hasNoResults) {
      return (
        <>
          <div className={styles.header_top}>
            <div className={styles.header_top_left}>
              <h1 className={styles.header}>
                sss {`Coworking Spaces in ${locationName}`} {isVirtualOfficePage}
              </h1>
              <p className={styles.header_spaces_amount}>
                <div className={styles.header_pin} dangerouslySetInnerHTML={{ __html: pathToIcon }} />
                {`${spacesCount.numSpaces} results`}
              </p>
            </div>
            {!isTablet && view == 'list' && <div className={styles.header_top_right}>
              <ViewToggle />
            </div>}
          </div>
          <div className={styles.header_bottom}>
            <FiltersList
              fetchData={fetchData}
              handleDurationSelection={handleDurationSelection}
              onWorkspaceHandler={onWorkspaceHandler}
            />
          </div>
          <div className={styles.no_results}>
            <div className={styles.title}>Sorry, workspaces with these parameters do not exist</div>
            <div className={styles.img_container}>
              <img src={noResultsImage} alt="no_results_image" />
            </div>
          </div>
        </>
        
      )
    }

    return;
  };

  // (isTablet && view === "map") conditions below => not rendering when we have full screen map on mobile devices
  return (
    <div className={styles.container}>
      {(view === "map" || (view === "list" && isLaptop)) && (
        <FiltersPanel
          fetchData={fetchData}
          handleDurationSelection={handleDurationSelection}
          onWorkspaceHandler={onWorkspaceHandler}
          spaceCount={maxValue}
        />
      )}
      {/* @ts-ignore */}
      <div className={styles.container__content} id={view === 'map' ? styles.map : ''} style={{flexDirection: window.CRO_899 == 3 && view === 'map' || isTablet ? 'column' : 'row', marginTop: window.CRO_899 == 3 && view === 'map' ? '0' : ''}}>
        {view === 'list' && (
          <div className={styles.container__content__sidebar}>
            <SideFilters onWorkspaceHandler={onWorkspaceHandler} handleDurationSelection={handleDurationSelection} fetchData={fetchData} />
          </div>
        )}
        <div className={styles.container__content__results}>
          {renderContent()}
        </div>
        {view === 'map' && (
          // @ts-ignore
          <div className={styles.container__content__map} style={{width: window.CRO_899 == 3 ? '100%' : ''}}>
            <Map
              currentMarker={currentMarker}
              fetchData={fetchData}
              getMembershipPrices={getMembershipPrices}
              markers={markers}
              generalData={generalData}
              setGeneralData={setGeneralData}
              isLoading={isLoading}
              mapCenterNo={mapCenterNo}
              durationOptions={durationOptions}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default observer(ResultsListRedesign);
